/**
 * Chart.js readouts. Sparkline stays a tiny SVG in currentColor (accent),
 * never the old #2ee6d6 teal area/donut.
 */

import type { ReactNode } from "react";
import { Bar, Doughnut } from "react-chartjs-2";

import { cx } from "./ui";
import {
  SURFACE,
  barOptions,
  doughnutOptions,
  registerCharts,
} from "./chartTheme";

registerCharts();

export function Sparkline({
  values,
  className,
  width = 88,
  height = 32,
}: {
  values: number[];
  className?: string;
  width?: number;
  height?: number;
}) {
  const pts = values.length ? values : [0, 0];
  const max = Math.max(...pts, 1);
  const min = Math.min(...pts, 0);
  const span = Math.max(max - min, 1);
  const step = pts.length > 1 ? (width - 4) / (pts.length - 1) : width - 4;
  const d = pts
    .map((value, index) => {
      const x = 2 + index * step;
      const y = height - 3 - ((value - min) / span) * (height - 8);
      return `${index === 0 ? "M" : "L"}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cx("overflow-visible text-accent", className)}
      aria-hidden
    >
      <path d={d} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

export function ChartFrame({
  ariaLabel,
  className = "h-[220px]",
  children,
}: {
  ariaLabel: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cx("relative min-w-0 w-full", className)} role="img" aria-label={ariaLabel}>
      {children}
    </div>
  );
}

export function BarChart({
  labels,
  datasets,
  horizontal = false,
  percent = false,
  legend = true,
  max,
}: {
  labels: string[];
  datasets: Array<{ label: string; data: number[]; color: string | string[] }>;
  horizontal?: boolean;
  percent?: boolean;
  legend?: boolean;
  max?: number;
}) {
  if (!labels.length || !datasets.length) return null;
  return (
    <Bar
      data={{
        labels,
        datasets: datasets.map((dataset) => ({
          label: dataset.label,
          data: dataset.data,
          backgroundColor: dataset.color,
          borderColor: "transparent",
          borderWidth: 0,
          borderRadius: 3,
          maxBarThickness: 22,
        })),
      }}
      options={barOptions({ horizontal, percent, legend, max })}
    />
  );
}

export function DoughnutChart({
  slices,
  legend = true,
}: {
  slices: Array<{ label: string; value: number; color: string }>;
  legend?: boolean;
}) {
  const total = slices.reduce((sum, slice) => sum + (Number.isFinite(slice.value) ? slice.value : 0), 0);
  if (total <= 0) return null;
  return (
    <Doughnut
      data={{
        labels: slices.map((slice) => slice.label),
        datasets: [
          {
            data: slices.map((slice) => slice.value),
            backgroundColor: slices.map((slice) => slice.color),
            borderColor: SURFACE,
            borderWidth: 1,
            hoverOffset: 4,
          },
        ],
      }}
      options={doughnutOptions(legend)}
    />
  );
}
